import { useEffect, useMemo, useRef, useState } from "preact/hooks"
import { History, Plus } from "lucide-react"
import useSWR from "swr"
import { api_fetch } from "@/api/client"
import { getProductDisplayLabel, ProductSearchResult, searchProducts } from "@/api/product_catalog"
import { use_debounced_value } from "@/hooks/use_debounced_value"

/** How long typing must pause before the catalog is searched. */
const SEARCH_DEBOUNCE_MS = 200

interface ProductMultiSelectProps {
  label: string
  selectedIds: string[]
  selectedLabels: string[]
  onChange: (selectedIds: string[], selectedLabels: string[]) => void
  required?: boolean
  /** Offer "All products" for deals at the retailer level (flat fees, accruals). */
  allowAllProducts?: boolean
  help?: string
  /** Called with the catalog product just added, so the form can read its case pack and prices. */
  onProductPicked?: (product: ProductSearchResult) => void
  /** Outline the field in red: products are required and none is picked, after the rep tried to save. */
  error?: boolean
  /** Anchor so the field can be scrolled to when it is missing. */
  id?: string
}

/** Label stored for a deal that covers every product rather than specific items. */
export const ALL_PRODUCTS_LABEL = "All products"

interface SelectedProductChip {
  id?: string
  label: string
}

function buildSelectedChips(selectedIds: string[], selectedLabels: string[]): SelectedProductChip[] {
  const maxLength = Math.max(selectedIds.length, selectedLabels.length)
  return Array.from({ length: maxLength }, (_, index) => ({
    id: selectedIds[index],
    label: selectedLabels[index] || selectedIds[index] || "Unknown product",
  })).filter((chip) => chip.label.trim().length > 0)
}

function productMatchesSelection(product: ProductSearchResult, selectedIds: string[], selectedLabels: string[]): boolean {
  return selectedIds.includes(product.id) || selectedLabels.some((label) => label.toLowerCase() === product.name.toLowerCase())
}

function containsCaseInsensitive(haystack: string[], needle: string): boolean {
  const normalized = needle.trim().toLowerCase()
  return haystack.some((candidate) => candidate.trim().toLowerCase() === normalized)
}

/**
 * Previously used product labels matching the query, excluding labels already selected and
 * labels that duplicate a catalog product already shown in the suggestions. An empty query
 * surfaces the reusable labels up front so they can be re-selected without retyping.
 */
export function filterPreviousLabelSuggestions(
  previousLabels: string[],
  query: string,
  selectedLabels: string[],
  catalogNames: string[],
  limit = 8,
): string[] {
  const normalizedQuery = query.trim().toLowerCase()
  return previousLabels
    .filter((label) => label.toLowerCase().includes(normalizedQuery))
    .filter((label) => !containsCaseInsensitive(selectedLabels, label))
    .filter((label) => !containsCaseInsensitive(catalogNames, label))
    .slice(0, limit)
}

/** Offer "add as a new product label" whenever the typed text matches nothing that already exists. */
export function shouldOfferNewLabel(
  query: string,
  previousLabels: string[],
  selectedLabels: string[],
  catalogNames: string[],
): boolean {
  const trimmed = query.trim()
  if (!trimmed) return false
  return (
    !containsCaseInsensitive(previousLabels, trimmed) &&
    !containsCaseInsensitive(selectedLabels, trimmed) &&
    !containsCaseInsensitive(catalogNames, trimmed)
  )
}

/** Catalog products matching the query, without their customer prices (the picker shows none). */
async function fetch_product_suggestions(query: string): Promise<ProductSearchResult[]> {
  const response = await searchProducts(query, 25, { include_prices: false })
  if (!response.ok) throw response.error
  return response.value.data
}

async function fetchPreviousProductLabels(): Promise<string[]> {
  const response = await api_fetch<string[]>("/promotions/product-labels")
  return response.ok ? response.value.data : []
}

export function ProductMultiSelect({
  label,
  selectedIds,
  selectedLabels,
  onChange,
  required = false,
  allowAllProducts = false,
  help,
  onProductPicked,
  error = false,
  id,
}: ProductMultiSelectProps) {
  const [query, setQuery] = useState("")
  // The results dropdown only shows while the search input is in use, so the suggestion list
  // can't be mistaken for products that are already selected (those are the chips above).
  const [open, setOpen] = useState(false)
  // Labels used on the org's other promotions, so previously typed products can be re-selected
  // instead of retyped. Loaded once, on the first dropdown open.
  const [previousLabels, setPreviousLabels] = useState<string[]>([])
  const previousLabelsLoadedRef = useRef(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const chips = useMemo(() => buildSelectedChips(selectedIds, selectedLabels), [selectedIds, selectedLabels])

  // One request per pause in typing, not one per keystroke: every letter of "protein" used to
  // start its own full catalog search on the server, and the answers to the first six were thrown
  // away. swr keys each response to the query that asked for it and remembers repeats.
  const debounced_query = use_debounced_value(query, SEARCH_DEBOUNCE_MS)
  const { data: results = [], isLoading: is_fetching } = useSWR(
    open ? ["promotion-product-search", debounced_query] : null,
    () => fetch_product_suggestions(debounced_query),
    { revalidateOnFocus: false },
  )
  const isLoading = is_fetching || debounced_query !== query

  useEffect(() => {
    if (!open || previousLabelsLoadedRef.current) return
    previousLabelsLoadedRef.current = true
    let isCurrent = true
    fetchPreviousProductLabels().then((labels) => {
      if (isCurrent) setPreviousLabels(labels)
    })
    return () => {
      isCurrent = false
    }
  }, [open])

  useEffect(() => {
    if (!open) return
    function handleClickOutside(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setOpen(false)
      }
    }
    // Escape closes the list wherever focus is — after clicking a suggestion it sits on that
    // suggestion's button, not on the search input, so the input's own key handler never saw it.
    function handleEscape(event: KeyboardEvent) {
      if (event.key === "Escape") setOpen(false)
    }
    document.addEventListener("mousedown", handleClickOutside)
    document.addEventListener("keydown", handleEscape)
    return () => {
      document.removeEventListener("mousedown", handleClickOutside)
      document.removeEventListener("keydown", handleEscape)
    }
  }, [open])

  // Picking closes the list so it stops covering the fields and the Create button below; clicking
  // the search box (or typing) opens it again for the next product.
  function handleSelect(product: ProductSearchResult) {
    if (productMatchesSelection(product, selectedIds, selectedLabels)) return
    onChange([...selectedIds, product.id], [...selectedLabels, product.name])
    onProductPicked?.(product)
    setQuery("")
    setOpen(false)
  }

  function handleAddLabel(labelToAdd: string) {
    const trimmed = labelToAdd.trim()
    if (!trimmed || containsCaseInsensitive(selectedLabels, trimmed)) return
    onChange(selectedIds, [...selectedLabels, trimmed])
    setQuery("")
    setOpen(false)
  }

  function handleRemove(chip: SelectedProductChip) {
    const nextIds = selectedIds.filter((id) => id !== chip.id)
    const nextLabels = selectedLabels.filter((label) => label !== chip.label)
    onChange(nextIds, nextLabels)
  }

  const filteredResults = results.filter((product) => !productMatchesSelection(product, selectedIds, selectedLabels))
  const catalogNames = filteredResults.map((product) => product.name)
  const previousLabelSuggestions = filterPreviousLabelSuggestions(previousLabels, query, selectedLabels, catalogNames)
  const offerNewLabel = shouldOfferNewLabel(query, previousLabelSuggestions, selectedLabels, catalogNames)

  return (
    <div class="space-y-2" ref={containerRef} id={id}>
      <label class={`block text-sm font-medium ${error ? "text-red-700" : "text-gray-700"}`}>
        {label} {required && <span class="text-red-500">*</span>}
        {chips.length > 0 && <span class="ml-2 font-normal text-xs text-gray-500">{chips.length} selected</span>}
      </label>
      <div class="relative">
        <div class={`flex flex-wrap gap-2 rounded-md border p-2 ${error ? "border-red-400 bg-red-50" : "border-gray-300 bg-white"}`}>
          {chips.map((chip) => (
            <button
              key={`${chip.id || "legacy"}-${chip.label}`}
              type="button"
              class="inline-flex items-center gap-1 rounded-full bg-blue-50 px-2 py-1 text-xs font-medium text-blue-700 hover:bg-blue-100"
              onClick={() => handleRemove(chip)}
              title="Remove product"
            >
              <span>{chip.label}</span>
              <span aria-hidden="true">x</span>
            </button>
          ))}
          <input
            value={query}
            onFocus={() => setOpen(true)}
            onClick={() => setOpen(true)}
            onInput={(event) => {
              setQuery(event.currentTarget.value)
              setOpen(true)
            }}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                event.preventDefault()
                handleAddLabel(query)
              }
              if (event.key === "Escape") {
                setOpen(false)
              }
            }}
            class="min-w-[220px] flex-1 border-0 p-1 text-sm outline-none focus:ring-0"
            placeholder={chips.length === 0 ? "Search products by name, UPC, code, line, or group" : "Search to add more products..."}
          />
        </div>
        {/* Overlaid dropdown (not inline) so suggestions read as options to add, never as selections. */}
        {open && (
          <div class="absolute left-0 right-0 top-full z-30 mt-1 max-h-56 overflow-y-auto rounded-md border border-gray-200 bg-white shadow-lg">
            <div class="border-b border-gray-100 px-3 py-1.5 text-[11px] uppercase tracking-wide text-gray-400">
              Suggestions — click to add
            </div>
            {isLoading && <div class="px-3 py-2 text-sm text-gray-500">Searching products...</div>}
            {!isLoading &&
              filteredResults.map((product) => (
                <button
                  key={product.id}
                  type="button"
                  class="flex w-full items-start gap-2 px-3 py-2 text-left text-sm hover:bg-blue-50"
                  onClick={() => handleSelect(product)}
                >
                  <Plus className="mt-0.5 h-3.5 w-3.5 shrink-0 text-gray-400" />
                  <span class="min-w-0">
                    <span class="block font-medium text-gray-900">{getProductDisplayLabel(product)}</span>
                    <span class="block text-xs text-gray-500">{product.identifiers.slice(0, 3).join(", ")}</span>
                  </span>
                </button>
              ))}
            {!isLoading && previousLabelSuggestions.length > 0 && (
              <>
                <div class="border-y border-gray-100 bg-gray-50 px-3 py-1 text-[11px] uppercase tracking-wide text-gray-400">
                  Previously used labels
                </div>
                {previousLabelSuggestions.map((previousLabel) => (
                  <button
                    key={previousLabel}
                    type="button"
                    class="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-blue-50"
                    onClick={() => handleAddLabel(previousLabel)}
                  >
                    <History className="h-3.5 w-3.5 shrink-0 text-gray-400" />
                    <span class="text-gray-900">{previousLabel}</span>
                  </button>
                ))}
              </>
            )}
            {!isLoading && offerNewLabel && (
              <button
                type="button"
                class="flex w-full items-center gap-2 border-t border-gray-100 px-3 py-2 text-left text-sm text-gray-600 hover:bg-gray-50"
                onClick={() => handleAddLabel(query)}
              >
                <Plus className="h-3.5 w-3.5 shrink-0 text-gray-400" />
                Add "{query.trim()}" as a new product label
              </button>
            )}
            {!isLoading && filteredResults.length === 0 && previousLabelSuggestions.length === 0 && !offerNewLabel && (
              <div class="px-3 py-2 text-sm text-gray-500">Type to search your product catalog.</div>
            )}
          </div>
        )}
      </div>
      {allowAllProducts && chips.length === 0 && (
        <button
          type="button"
          class="text-xs font-medium text-blue-600 hover:text-blue-800"
          onClick={() => handleAddLabel(ALL_PRODUCTS_LABEL)}
        >
          + All products (not tied to specific items)
        </button>
      )}
      {help && <p class="text-xs text-gray-500">{help}</p>}
    </div>
  )
}

if (import.meta.vitest) {
  const { describe, expect, it } = import.meta.vitest

  describe("buildSelectedChips", () => {
    it("keeps legacy labels even without product ids", () => {
      expect(buildSelectedChips([], ["All Products"])).toEqual([{ id: undefined, label: "All Products" }])
    })

    it("pairs selected ids and labels by index", () => {
      expect(buildSelectedChips(["p1"], ["Calamansi"])).toEqual([{ id: "p1", label: "Calamansi" }])
    })
  })

  describe("filterPreviousLabelSuggestions", () => {
    const labels = ["Sweet Vanilla Bean", "Southern Roasted Pecan", "Lonestar Original"]

    it("matches labels by substring, case-insensitively", () => {
      expect(filterPreviousLabelSuggestions(labels, "vanilla", [], [])).toEqual(["Sweet Vanilla Bean"])
    })

    it("shows reusable labels before anything is typed", () => {
      expect(filterPreviousLabelSuggestions(labels, "", [], [])).toEqual(labels)
    })

    it("hides labels already selected or duplicating catalog products", () => {
      expect(filterPreviousLabelSuggestions(labels, "", ["sweet vanilla bean"], ["Lonestar Original"])).toEqual([
        "Southern Roasted Pecan",
      ])
    })

    it("caps the list", () => {
      expect(filterPreviousLabelSuggestions(labels, "", [], [], 2)).toHaveLength(2)
    })
  })

  describe("shouldOfferNewLabel", () => {
    it("offers a new label for unmatched text even when other suggestions exist", () => {
      expect(shouldOfferNewLabel("vanilla", ["Sweet Vanilla Bean"], [], ["Vanilla Latte 12oz"])).toBe(true)
    })

    it("does not offer duplicates of existing labels, selections, or catalog names", () => {
      expect(shouldOfferNewLabel("sweet vanilla bean", ["Sweet Vanilla Bean"], [], [])).toBe(false)
      expect(shouldOfferNewLabel(" Vanilla ", [], ["vanilla"], [])).toBe(false)
      expect(shouldOfferNewLabel("Lonestar Original", [], [], ["lonestar original"])).toBe(false)
    })

    it("stays hidden for empty input", () => {
      expect(shouldOfferNewLabel("   ", [], [], [])).toBe(false)
    })
  })
}
